import AIChatWidget from '@/components/aichat/AiChatWidget';
import PageHero from '@/components/landing/page-hero';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');
const aboutSections = [
    { label: 'School Seal & Motto', href: '/about/school-seal' },
    { label: 'History', href: '/about/history' },
    { label: 'Vision & Mission', href: '/about/vision-and-mission' },
    { label: 'Educational Philosophy', href: '/about/educational-philosophy' },
];

interface AboutPageShellProps {
    title: string;
    description: string;
    seoDescription?: string;
    icon?: LucideIcon;
    children: ReactNode;
    showSectionMenu?: boolean;
}

export default function AboutPageShell({ title, description, seoDescription, icon, children, showSectionMenu = true }: AboutPageShellProps) {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    return (
        <LandingLayout>
            <SeoHead title={title} description={seoDescription ?? description} image={`${baseUrl}/images/logo.webp`} />

            <section className="bg-muted pb-16">
                <PageHero
                    title={title}
                    description={description}
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        ...(title === 'About Northeastern College' ? [] : [{ label: title }]),
                    ]}
                    image="/images/hero/about.png"
                    imageAlt="Northeastern College campus"
                    icon={icon}
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    {showSectionMenu ? (
                        <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
                            <aside className="lg:sticky lg:top-28">
                                <div className="bg-card border-border rounded-2xl border p-5 shadow-sm">
                                    <div className="mb-4 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                        About Sections
                                    </div>
                                    <div className="space-y-2">
                                        {aboutSections.map((section) => {
                                            const isActive = currentPath === section.href;

                                            return (
                                                <Link
                                                    key={section.href}
                                                    href={section.href}
                                                    prefetch
                                                    className={`block rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                                                        isActive
                                                            ? 'bg-[#0A7A35] text-white shadow-sm'
                                                            : 'text-foreground hover:bg-[#0A7A35]/8 hover:text-[#0A7A35]'
                                                    }`}
                                                >
                                                    {section.label}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            </aside>

                            <div>{children}</div>
                        </div>
                    ) : (
                        children
                    )}
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
